import type { GetStaticProps } from 'next'
import type { FC } from 'react'
import type { Category } from '@/types'

import { Seo } from '@/components/generals/seo'
import { SiteLayout } from '@/components/layout/site-layout'
import { LegalPage } from '@/components/legal/legal-page'

import { getLayoutData } from '@/lib/layout-data'

interface TermsProps {
    categories: Category[]
}

const DESCRIPTION =
    'The terms and conditions that govern your use of the Milano website and your purchases.'

const Terms: FC<TermsProps> = ({ categories }) => {
    return (
        <SiteLayout categories={categories}>
            <Seo title='Terms & Conditions' description={DESCRIPTION} />

            <LegalPage title='Terms & Conditions' updated='June 2026'>
                <p>
                    These Terms &amp; Conditions govern your access to and use of the Milano website and the
                    purchase of any products from us. By using our site or placing an order, you agree to be
                    bound by these terms.
                </p>

                <h3>Use of the Site</h3>
                <p>
                    You agree to use the site only for lawful purposes and not to engage in any activity that
                    could damage, disable, or impair its operation. All content on this site — including text,
                    images, and logos — is the property of Milano and may not be reproduced without permission.
                </p>

                <h3>Orders &amp; Pricing</h3>
                <ul role='list'>
                    <li>All orders are subject to acceptance and availability.</li>
                    <li>Prices are shown in your local currency and may change without notice.</li>
                    <li>We reserve the right to refuse or cancel any order, including for pricing errors.</li>
                </ul>

                <h3>Shipping &amp; Returns</h3>
                <p>
                    Delivery times are estimates and not guaranteed. Risk of loss passes to you upon delivery.
                    Returns are handled in accordance with our Return and Guarantee policies — please review
                    those pages for full details.
                </p>

                <h3>Limitation of Liability</h3>
                <p>
                    To the fullest extent permitted by law, Milano shall not be liable for any indirect,
                    incidental, or consequential damages arising from your use of the site or products
                    purchased through it.
                </p>

                <h3>Changes to These Terms</h3>
                <p>
                    We may update these Terms &amp; Conditions from time to time. Continued use of the site
                    after changes are posted constitutes your acceptance of the revised terms.
                </p>

                <h3>Contact Us</h3>
                <p>
                    Questions about these terms? Contact us at
                    {' '}
                    <a href='mailto:hello@milano.clothes'>hello@milano.clothes</a>.
                </p>
            </LegalPage>
        </SiteLayout>
    )
}

export const getStaticProps: GetStaticProps<TermsProps> = async () => {
    const { categories } = getLayoutData()

    return {
        props: {
            categories
        }
    }
}

export default Terms
